import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "state/rootReducer";
import { AppThunk } from "state/store";
import request from "superagent";
import { API_HOST } from "shared/config";
import { SpotifyTrack } from "react-spotify-web-playback";
import { SongFeedbackAndSeed } from "./userSlice";

const SPOTIFY_TRACK_ENDPOINT = "https://api.spotify.com/v1/tracks/";

/** A song's artwork and Spotify links, fetched when its detail page opens. */
export interface SongArt {
  artUri: string | null;
  spotifyUri: string | null;
  artistUris: string[];
  album: string | null;
  albumUri: string | null;
}

interface SongsState {
  /**
   * Cached per song, so going back to a song doesn't refetch. Which song is
   * showing is no longer store state: it's the /songs/:gid route, so the
   * browser's history is the back stack.
   */
  songArtByGid: Record<string, SongArt>;
  playlist: Song[] | null;
  offset: number | null;
  isPlaying: boolean;
  /**
   * Counts presses of play in the app. This store is persisted, so a restored
   * `isPlaying` can't tell the player to start. A count that has gone up since
   * the page loaded can.
   */
  playRequest: number;
  currentTrackId: string | null;
  playerTrack: SpotifyTrack | null;
  songsByGid: Record<string, Song>;
  songRecsByGid: Record<string, SongRecommendation[]>;
  currentSearch: string;
  searchResults: Song[] | null;
  songRecsFromLikes: SongRecommendation[] | null;
}

const initialState: SongsState = {
  songArtByGid: {},
  playlist: null,
  offset: null,
  isPlaying: false,
  playRequest: 0,
  currentTrackId: null,
  songsByGid: {},
  songRecsByGid: {},
  currentSearch: "",
  searchResults: null,
  songRecsFromLikes: null,
  playerTrack: null,
};

const songsSlice = createSlice({
  name: "songs",
  initialState,
  reducers: {
    receivedSongs(state, action: PayloadAction<Song[]>) {
      state.songsByGid = {};
      action.payload.forEach((song) => {
        if (!state.songsByGid[song.gid]) {
          state.songsByGid[song.gid] = song;
        }
      });

      console.log(`Done loading ${action.payload.length} songs.`);
    },
    receivedSearchResult(state, action: PayloadAction<Song[] | null>) {
      state.searchResults = action.payload;
    },
    searchStringUpdated(state, action: PayloadAction<string>) {
      state.currentSearch = action.payload;
    },
    receivedRecommendations(
      state,
      action: PayloadAction<{ forSong: Song; recs: SongRecommendation[] }>
    ) {
      const { forSong, recs } = action.payload;

      state.songRecsByGid[forSong.gid] = recs;
    },
    receivedRecommendationsFromLikes(
      state,
      action: PayloadAction<SongRecommendation[] | null>
    ) {
      state.songRecsFromLikes = action.payload;
    },
    receivedSongArt(
      state,
      action: PayloadAction<{ gid: string; art: SongArt }>
    ) {
      // A store persisted before this field existed loads without it.
      state.songArtByGid ??= {};
      state.songArtByGid[action.payload.gid] = action.payload.art;
    },
    addToPlaylist(state, action: PayloadAction<Song[]>) {
      if (state.playlist === null) {
        state.playlist = [];
      }

      state.playlist = action.payload.filter((s) => s.spotify_id);
      state.isPlaying = true;
      // A store persisted before this field existed loads without it.
      state.playRequest = (state.playRequest ?? 0) + 1;
      state.offset = 0;
      state.currentTrackId = state.playlist[0].gid;

      console.log(
        "Playing is now: " + state.playlist.map((s) => s.name).join(",")
      );
      console.log("Playing : " + state.isPlaying);
    },
    setPlayingStatus(
      state,
      action: PayloadAction<{
        isPlaying: boolean;
        playingNow: SpotifyTrack;
      }>
    ) {
      const { isPlaying, playingNow } = action.payload;

      state.playerTrack = playingNow;

      state.isPlaying = isPlaying;

      let playing = state.playlist?.findIndex(
        (s) => s.spotify_id === playingNow.id
      );

      if (playing === -1) {
        console.log(
          `Cant find matching spotify ID, searching... in ${JSON.stringify(
            state.playlist?.map((s) => s.name)
          )}`
        );

        playing = state.playlist?.findIndex(
          (s) =>
            playingNow.name.toLowerCase().includes(s.name.toLowerCase()) ||
            s.name.toLowerCase().includes(playingNow.name.toLowerCase())
        );
      }

      if (playing !== undefined && playing !== -1 && state.playlist) {
        state.offset = playing;
        state.currentTrackId = state.playlist[playing].gid;
        console.log(
          `Setting currentTrackId to ${state.currentTrackId} (${state.playlist[playing].name})`
        );
      }
    },
  },
});

function getGids(source: Record<string, SongFeedbackAndSeed>): string {
  const seeds: string[] = [];

  for (const [gid, sr] of Object.entries(source)) {
    if (sr.seed) {
      seeds.push(gid);
    }
  }

  return seeds.join(",");
}

async function fetchArtUri(
  song: Song,
  token: string
): Promise<{
  albumArtUrl: string | null;
  artistUrls: string[];
  trackUrl: string | null;
  album: string | null;
  albumUri: string | null;
}> {
  try {
    const trackResponse = await request
      .get(SPOTIFY_TRACK_ENDPOINT + song.spotify_id)
      .set("Authorization", "Bearer " + token)
      .set("accept", "application/json");
    const result = JSON.parse(trackResponse.text);

    return {
      albumArtUrl: result?.album?.images[0]?.url || null,
      artistUrls:
        result?.artists?.map((a: any) => a.external_urls?.spotify) || [],
      trackUrl: result?.external_urls?.spotify || null,
      album: result?.album?.name,
      albumUri: result?.album?.external_urls?.spotify,
    };
  } catch (e) {
    console.error(e);

    return {
      albumArtUrl: null,
      artistUrls: [],
      trackUrl: null,
      album: null,
      albumUri: null,
    };
  }
}

export const actions = {
  ...songsSlice.actions,
  fetchAllSongs(): AppThunk {
    return async (dispatch, _getState) => {
      let cleanedResponse: Song[];

      try {
        console.log(
          `Platform ${process.env.NODE_ENV} resolved to API host ${API_HOST}`
        );

        console.log("Fetching ALL songs from server...");

        const response = await request
          .get(`${API_HOST}/v1/songs`)
          .set("Authorization", "Bearer " + _getState().user.vybeToken)
          .set("accept", "application/json");

        cleanedResponse = JSON.parse(response.text);

        console.log(
          `${cleanedResponse.length} songs fetched. Loading into store...`
        );

        dispatch(actions.receivedSongs(cleanedResponse));
      } catch (e) {
        console.error(e);

        return null;
      }
    };
  },
  setSearchString(searchString: string, callback: () => void): AppThunk {
    return async (dispatch, _getState) => {
      console.log("Set search string to: " + searchString);

      let cleanedResponse: Song[];

      try {
        if (searchString.trim().length === 0) {
          console.log("Fetching some songs");

          const response = await request
            .get(`${API_HOST}/v1/songs`)
            .set("Authorization", "Bearer " + _getState().user.vybeToken)
            .set("accept", "application/json");

          cleanedResponse = JSON.parse(response.text);
        } else {
          console.log("Searching for some songs matching: " + searchString);

          const response = await request
            .get(
              `${API_HOST}/v1/songs?search=${encodeURIComponent(searchString)}`
            )
            .set("Authorization", "Bearer " + _getState().user.vybeToken)
            .set("accept", "application/json");

          cleanedResponse = JSON.parse(response.text);
        }

        console.log(
          `${cleanedResponse.length} songs fetched. Loading into store...`
        );

        dispatch(actions.receivedSongs(cleanedResponse));
        dispatch(actions.receivedSearchResult(cleanedResponse));
        dispatch(actions.searchStringUpdated(searchString));

        console.log("Done updating search string");
      } catch (e) {
        console.error(e);
      } finally {
        // Always settle. The caller's spinner waits on this, and returning
        // early on error used to leave it spinning forever.
        callback();
      }
    };
  },
  fetchRecommendations(
    song: Song,
    search: boolean,
    algo: "v1" | "v2"
  ): AppThunk {
    return async (dispatch, _getState) => {
      let cleanedResponse: SongRecommendation[];

      console.log(`Fetching recs for ${song.name} (${song.gid})`);

      const recCount = 250;

      try {
        const response = await request
          .get(
            `${API_HOST}/v1/songs/recommendations/${song.gid}?search=${search}&filterSameArtist=true&recCount=${recCount}&algo=${algo}`
          )
          .set("Authorization", "Bearer " + _getState().user.vybeToken)
          .set("accept", "application/json");

        cleanedResponse = JSON.parse(response.text);

        dispatch(actions.receivedSongs(cleanedResponse.map((sr) => sr.song)));
        dispatch(
          actions.receivedRecommendations({
            forSong: song,
            recs: cleanedResponse,
          })
        );
      } catch (e) {
        console.error(e);

        return null;
      }
    };
  },
  fetchRecommendationsFromLikes(
    numItems: number,
    depth: number,
    top: boolean,
    includeSeeds: boolean,
    includeLikes: boolean,
    popPenalty: number,
    algo: "v1" | "v2"
  ): AppThunk {
    return async (dispatch, _getState) => {
      const userId = _getState().user.currentUser?.id;

      if (!userId) {
        console.log(`User not logged in - can't generate recs from likes.`);
      }

      let cleanedResponse: SongRecommendation[];

      console.log(`Fetching recs from likes for ${userId}...`);

      try {
        const seeds = _getState().user.songFeedbackByGid;

        const response = await request
          .get(
            `${API_HOST}/v1/user/${userId}/recommendations?numItems=${numItems}&depth=${depth}&top=${top}&popPenalty=${popPenalty}&includeSeeds=${includeSeeds}&includeLikes=${includeLikes}${
              seeds ? `&seeds=${getGids(seeds)}` : ""
            }&algo=${algo}`
          )
          .set("Authorization", "Bearer " + _getState().user.vybeToken)
          .set("accept", "application/json");

        cleanedResponse = JSON.parse(response.text);

        dispatch(actions.receivedSongs(cleanedResponse.map((sr) => sr.song)));
        dispatch(actions.receivedRecommendationsFromLikes(cleanedResponse));
      } catch (e) {
        console.error(e);

        return null;
      }
    };
  },
  clearRecommendationsFromLikes(): AppThunk {
    return async (dispatch, _) => {
      dispatch(actions.receivedRecommendationsFromLikes(null));
    };
  },
  searchForMoreResults(callback: () => void): AppThunk {
    return async (dispatch, _getState) => {
      let cleanedResponse: Song[];

      try {
        console.log(
          `Attempting to search for: ${_getState().songs.currentSearch}`
        );

        const response = await request
          .post(`${API_HOST}/v1/add_search`)
          .set("Authorization", "Bearer " + _getState().user.vybeToken)
          .set("accept", "application/json")
          .field("searchTerm", _getState().songs.currentSearch);

        cleanedResponse = JSON.parse(response.text);

        dispatch(actions.receivedSongs(cleanedResponse));
      } catch (e) {
        console.error(e);
        // The refresh below is what calls `callback`; skipping it on error
        // used to leave the "More from Spotify" spinner running forever.
        callback();

        return null;
      }

      // Re-run the current search so the newly added tracks show. It calls
      // `callback` when it settles, whether or not it succeeds.
      dispatch(
        actions.setSearchString(_getState().songs.currentSearch, callback)
      );
    };
  },
  /**
   * Run when a song's detail page opens: fetches its artwork and Spotify links
   * unless they're already cached. It no longer queues the song: the player is
   * mounted from login and stays hidden until something is played.
   */
  viewSong(song: Song): AppThunk {
    return async (dispatch, _getState) => {
      const token = _getState().user.accessToken;

      if (token === null || selectors.getSongArt(_getState(), song.gid)) {
        return;
      }

      const result = await fetchArtUri(song, token);

      // fetchArtUri returns all nulls on failure. Caching that would stop a
      // later visit from retrying.
      if (result.trackUrl === null) {
        return;
      }

      dispatch(
        actions.receivedSongArt({
          gid: song.gid,
          art: {
            artUri: result.albumArtUrl,
            spotifyUri: result.trackUrl,
            artistUris: result.artistUrls,
            album: result.album,
            albumUri: result.albumUri,
          },
        })
      );
    };
  },
};

function songMatchesSearch(song: Song, search: string): boolean {
  return true;
  const searchTokens = search.split(" ");

  const toSearch: string[] = [
    ...song.name.split(" "),
    ...song.artists.flatMap((a) => a.name.split(" ")),
  ];

  for (const t1 of searchTokens) {
    let searchTermMatched = false;

    for (const t2 of toSearch) {
      searchTermMatched =
        searchTermMatched || t2.toLowerCase().includes(t1.toLowerCase());
    }

    if (!searchTermMatched) {
      return false;
    }
  }

  return true;
}

const songs = (state: RootState) => state.songs.songsByGid;
const numberOfSongs = (state: RootState) =>
  Object.keys(state.songs.songRecsByGid).length;
const searchTerm = (state: RootState) => state.songs.currentSearch.trim();

export const selectors = {
  getAllSongs: createSelector(
    [songs, numberOfSongs, searchTerm],
    (songs, numberOfSongs, searchTerm) => {
      if (searchTerm.length === 0) {
        console.log("Retrieving songs...");

        const result = Object.values(songs);

        console.log(`Got ${result.length} songs.`);

        return result;
      } else {
        console.log("Searching for all songs matching: " + searchTerm);

        const result = Object.values(songs).filter((s) =>
          songMatchesSearch(s, searchTerm)
        );

        console.log("Done searching songs.");

        return result;
      }
    }
  ),
  getSongsServerSide(state: RootState): Song[] | null {
    return state.songs.searchResults;
  },
  getRecommendations(
    state: RootState,
    songGid: string
  ): SongRecommendation[] | null {
    return state.songs.songRecsByGid[songGid] || null;
  },
  getPlaylist(state: RootState): Song[] | null {
    return state.songs.playlist;
  },
  getSongArt(state: RootState, songGid: string): SongArt | null {
    return state.songs.songArtByGid?.[songGid] ?? null;
  },
  /**
   * Finds a song anywhere the client has loaded one. For a song page opened
   * without the song in history state, such as a pasted link.
   */
  findSong(state: RootState, songGid: string): Song | null {
    return (
      state.songs.songsByGid[songGid] ??
      state.user.songFeedbackByGid?.[songGid]?.song ??
      state.songs.songRecsFromLikes?.find((sr) => sr.song.gid === songGid)
        ?.song ??
      Object.values(state.songs.songRecsByGid)
        .flat()
        .find((sr) => sr.song.gid === songGid)?.song ??
      null
    );
  },
};

export default songsSlice.reducer;
